import { createContext, useContext, type ReactNode } from 'react'
import styles from './Bracket.module.css'

/**
 * Whether the bracket is a ranked playoff or a random-draw tournament. Playoffs
 * are seeded, so seed numbers are shown; tournaments are drawn at random, so
 * seed chips are meaningless and hidden.
 */
export type BracketVariant = 'playoff' | 'tournament'

const VariantContext = createContext<BracketVariant>('playoff')

export interface BracketTeam {
  name: string
  seed?: number
  score?: number | null
  /** Highlights the team as the match winner. */
  isWinner?: boolean
  /**
   * This side forfeited the match: its opponent won the series without it being
   * played out. Shows "FF" in place of a score.
   */
  forfeited?: boolean
}

export interface BracketMatchCell {
  id: string
  /** null = the slot is not yet determined (renders "TBD"). */
  home: BracketTeam | null
  away: BracketTeam | null
  /** Short date/label shown above the cell, e.g. "Aug 22". */
  date?: string
  /**
   * 0-indexed slot within the round. When a round sets `slots`, matches are laid
   * out by position so bye positions leave an aligned gap. Defaults to the array
   * index.
   */
  position?: number
}

export interface BracketRound {
  title: string
  matches: BracketMatchCell[]
  /**
   * Total slot count for the round. When set, the round renders `slots` evenly
   * sized slots and places each match at its `position` — empty slots (byes)
   * keep the bracket geometry aligned so connectors route correctly. Omit for a
   * dense round (e.g. a losers bracket) that renders its matches back-to-back.
   */
  slots?: number
}

export interface BracketSection {
  /** Optional heading, e.g. "Winners", "Losers". Omit for a single bracket. */
  title?: string
  rounds: BracketRound[]
}

export interface BracketProps {
  sections: BracketSection[]
  /** 'playoff' (seeded, shows seed numbers) or 'tournament' (random draw, no seeds). Defaults to 'playoff'. */
  variant?: BracketVariant
  className?: string
}

function Team({ team }: { team: BracketTeam | null }) {
  const variant = useContext(VariantContext)
  if (!team) {
    return (
      <div className={`${styles.team} ${styles.tbd}`}>
        <span className={styles.name}>TBD</span>
      </div>
    )
  }
  return (
    <div className={`${styles.team} ${team.isWinner ? styles.winner : ''} ${team.forfeited ? styles.forfeited : ''}`}>
      {variant === 'playoff' && team.seed != null && <span className={styles.seed}>{team.seed}</span>}
      <span className={styles.name}>{team.name}</span>
      {team.forfeited
        ? <abbr className={styles.score} title={`${team.name} forfeited`}>FF</abbr>
        : team.score != null && <span className={styles.score}>{team.score}</span>}
    </div>
  )
}

function MatchBox({ m }: { m: BracketMatchCell }) {
  return (
    <div className={styles.match}>
      {m.date && <div className={styles.date}>{m.date}</div>}
      <Team team={m.home} />
      <div className={styles.divider} />
      <Team team={m.away} />
    </div>
  )
}

// ─── Slot-based section (single elimination, bye-aware) ─────────────────────
//
// Each round is expanded to its full slot count. Real matches sit at their
// position; a bye position renders a "Bye" cell so every slot reserves the same
// height — this keeps all slots in a round equal (without it, empty slots
// collapse and the connector lines miss the boxes) and makes the first round
// read as a full bracket instead of a couple of floating matches. Because every
// round has exactly half the slots of the previous one and shares the same
// height, slot j sits at the vertical midpoint of its two feeder slots
// (2j, 2j+1), so connectors route center-to-center.

interface SlotRow {
  title: string
  slots: (BracketMatchCell | null)[]
}

function toSlotRow(round: BracketRound): SlotRow {
  /* v8 ignore next -- matches.length is the fallback slot count for a round without an explicit slot count */
  const count = round.slots ?? round.matches.length
  const slots: (BracketMatchCell | null)[] = Array.from({ length: count }, () => null)
  round.matches.forEach((m, i) => {
    const pos = m.position ?? i
    if (pos >= 0 && pos < slots.length) slots[pos] = m
    else slots.push(m) // out-of-range safety: never drop a match
  })
  return { title: round.title, slots }
}

// A round feeds the next only when it has exactly twice the slots (a clean halving).
function halves(from: SlotRow | undefined, to: SlotRow | undefined): boolean {
  return !!from && !!to && from.slots.length === to.slots.length * 2
}

// A bye position renders an invisible clone of a match cell: it reserves the
// same height (so every slot in the round stays equal and the connector lines
// stay aligned) without drawing anything on screen.
const GHOST: BracketMatchCell = { id: 'ghost', home: null, away: null, date: ' ' }

function SlottedSection({ rows }: { rows: SlotRow[] }) {
  return (
    <div className={styles.scroll}>
      <div className={styles.rounds}>
        {rows.map((row, ri) => {
          const next = rows[ri + 1]
          const prev = rows[ri - 1]
          const feedsNext = halves(row, next)
          const fedByPrev = halves(prev, row)
          return (
            <div key={ri} className={styles.round}>
              <div className={styles.roundTitle}>{row.title}</div>
              <div className={styles.matches}>
                {row.slots.map((m, si) => {
                  // Connectors are real elements (not ::before/::after) because a
                  // middle-round slot needs three segments at once — incoming
                  // stub, outgoing stub, and the vertical — which two pseudo-
                  // elements cannot provide without colliding.
                  const drawOut = feedsNext && m && !!next.slots[Math.floor(si / 2)]
                  /* v8 ignore next -- defensive branch not reachable in practice */
                  const drawIn = fedByPrev && m && !!(prev.slots[si * 2] || prev.slots[si * 2 + 1])
                  return (
                    <div key={m?.id ?? `bye-${si}`} className={styles.slot}>
                      {drawIn && <span className={styles.cIn} />}
                      {m ? <MatchBox m={m} /> : <div className={styles.ghost} aria-hidden="true"><MatchBox m={GHOST} /></div>}
                      {drawOut && <span className={styles.cOut} />}
                      {drawOut && <span className={`${styles.cVert} ${si % 2 === 0 ? styles.cDown : styles.cUp}`} />}
                    </div>
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ─── Dense section (legacy: losers brackets, ad-hoc rounds) ─────────────────

function DenseSection({ rounds }: { rounds: BracketRound[] }) {
  return (
    <div className={styles.scroll}>
      <div className={styles.rounds}>
        {rounds.map((round, ri) => (
          <div key={ri} className={styles.round}>
            <div className={styles.roundTitle}>{round.title}</div>
            <div className={styles.matches}>
              {round.matches.map((m) => (
                <div key={m.id} className={styles.matchWrap}>
                  <MatchBox m={m} />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

/**
 * Bracket — a single/double-elimination bracket view. Rounds render as columns
 * of match cells with connector lines between them; unresolved slots show "TBD".
 * Multiple sections (e.g. Winners / Losers / Grand Final) stack vertically, each
 * horizontally scrollable on small screens. Purely presentational.
 *
 * A section whose rounds carry `slots` renders bye-aware (empty slots keep the
 * geometry aligned); otherwise it renders its matches back-to-back.
 *
 * `variant` formats it as a ranked 'playoff' (seed numbers shown) or a
 * random-draw 'tournament' (seed numbers hidden).
 */
export function Bracket({ sections, variant = 'playoff', className }: BracketProps): ReactNode {
  return (
    <VariantContext.Provider value={variant}>
      <div className={[styles.bracket, className].filter(Boolean).join(' ')}>
        {sections.map((section, si) => {
          const slotted = section.rounds.some((r) => r.slots != null)
          return (
            <div key={si} className={styles.section}>
              {section.title && <div className={styles.sectionTitle}>{section.title}</div>}
              {slotted
                ? <SlottedSection rows={section.rounds.map(toSlotRow)} />
                : <DenseSection rounds={section.rounds} />}
            </div>
          )
        })}
      </div>
    </VariantContext.Provider>
  )
}
